import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Popover, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoundedButton } from 'src/components/RoundedButton';
import { UppercaseCaptionTypography } from 'src/features/visits/shared/components/UppercaseCaptionTypography';
import { SchoolWorkNoteExcuseDocFileDTO } from 'utils';
import { ExcuseLink } from './ExcuseLink';

type ExcuseCardProps = {
  label: string;
  excuse?: SchoolWorkNoteExcuseDocFileDTO & { presignedUrl?: string };
  isLoading: boolean;
  onDelete: (id: string) => void;
  onPublish: (id: string) => void;
  generateTemplateOpen: (value: boolean) => void;
  generateFreeOpen: (value: boolean) => void;
  disabled?: boolean;
};

export const ExcuseCard: FC<ExcuseCardProps> = (props) => {
  const { t } = useTranslation();
  const { label, excuse, isLoading, onDelete, onPublish, generateTemplateOpen, generateFreeOpen, disabled } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <UppercaseCaptionTypography>{label}</UppercaseCaptionTypography>

      {excuse && (
        <>
          <ExcuseLink
            label={excuse.name!}
            to={excuse.presignedUrl!}
            onDelete={disabled ? undefined : () => onDelete(excuse.id)}
            disabled={isLoading}
          />
          <Typography>
            {t('telemed.plan.excuse.generated')}:{' '}
            {DateTime.fromISO(excuse.date!).toLocaleString(DateTime.DATETIME_SHORT, {
              locale: t('telemed.plan.excuse.generated') === 'Generado' ? 'es-es' : 'en-us',
            })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'end' }}>
            <Box
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <InfoOutlinedIcon color="primary" />
            </Box>
            <RoundedButton
              variant="contained"
              disabled={excuse.published || isLoading || disabled}
              onClick={() => onPublish(excuse.id)}
            >
              {excuse.published ? t('telemed.plan.excuse.published') : t('telemed.plan.excuse.publishNow')}
            </RoundedButton>
          </Box>
          <Popover
            sx={{
              pointerEvents: 'none',
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Typography sx={{ p: 2, width: '300px' }}>{t('telemed.plan.excuse.publishTooltip')}</Typography>
          </Popover>
        </>
      )}

      {!excuse && (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <RoundedButton
            onClick={() => generateTemplateOpen(true)}
            disabled={isLoading || disabled}
            sx={{ fontSize: { xs: '12px', md: '14px' }, whiteSpace: 'normal', textAlign: 'center' }}
          >
            {t('telemed.plan.excuse.generateTemplate')}
          </RoundedButton>
          <RoundedButton
            onClick={() => generateFreeOpen(true)}
            disabled={isLoading || disabled}
            sx={{ fontSize: { xs: '12px', md: '14px' }, whiteSpace: 'normal', textAlign: 'center' }}
          >
            {t('telemed.plan.excuse.freeFormat')}
          </RoundedButton>
        </Box>
      )}
    </Box>
  );
};
